import { CheckRounded, PriorityHighRounded } from "@mui/icons-material";
import { Card, Chip, Divider, Link, Stack, styled, Tooltip, Typography } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import type { ReactNode } from "react";
import { FormRecipientType, FormSubmissionPrivacyType, Links } from "src/constants";
import { type FormSubmissionDto } from "src/http";
import { getFormSubmissionName } from "src/utilities";

const StyledCard = styled(Card)`
  position: relative;
  cursor: pointer;
  border: solid 1px ${({ theme }) => theme.palette.line.main};
  padding: ${({ theme }) => theme.spacing(1)};
  z-index: 2;

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.transparent5};
  }
`;

interface Props {
  formSubmission: FormSubmissionDto;
  onClicked?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, formSubmissionGuid: string) => void;
  onDraftClicked?: (formSubmissionGuid: string, formGuid: string) => void;
}

const UserFeedbackListItem = (props: Props) => {
  function getVisibility() {
    // Drafts are only visible to their creator
    if (props.formSubmission.submittedDateTimeUtc == null) {
      return (
        <Link href={Links.User(props.formSubmission.createdByUser.userId)} onClick={(e) => e.stopPropagation()}>
          {props.formSubmission.createdByUser.displayName}
        </Link>
      );
    }

    switch (props.formSubmission.privacySetting) {
      case FormSubmissionPrivacyType.Public:
        return "All";
      case FormSubmissionPrivacyType.RecipientAndPeopleLead: {
        const sender = props.formSubmission.createdByUser;
        const recipient = props.formSubmission.recipientUser;
        const recipientPeopleLead = props.formSubmission.recipientUserPeopleLead;
        const privilegedUsers = props.formSubmission.privilegedUsers;

        const result: ReactNode[] = [
          <Link key="sender" href={Links.User(sender.userId)} onClick={(e) => e.stopPropagation()}>
            {sender.displayName}
          </Link>,
        ];

        if (props.formSubmission.recipientType !== FormRecipientType.Self && recipient.userId !== sender.userId) {
          result.push(
            <span key="recipient">
              {", "}
              <Link href={Links.User(recipient.userId)} onClick={(e) => e.stopPropagation()}>
                {recipient.displayName}
              </Link>
            </span>,
          );
        }

        if (recipientPeopleLead != null && recipientPeopleLead.userId !== sender.userId) {
          result.push(
            <span key="recipientPeopleLead">
              {", "}
              <Tooltip
                title={
                  <Link href={Links.User(recipientPeopleLead.userId)} onClick={(e) => e.stopPropagation()}>
                    {recipientPeopleLead.displayName}
                  </Link>
                }
              >
                <Link href={Links.User(recipientPeopleLead.userId)} onClick={(e) => e.stopPropagation()}>
                  People Lead
                </Link>
              </Tooltip>
            </span>,
          );
        }

        if (privilegedUsers.length > 0) {
          result.push(
            <span key="privilegedUsers">
              {", "}
              <Tooltip
                title={privilegedUsers
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((user, index, array) => (
                    <span key={index}>
                      <Link href={Links.User(user.userId)} onClick={(e) => e.stopPropagation()}>
                        {user.displayName}
                      </Link>
                      {index < array.length - 1 && ", "}
                    </span>
                  ))}
              >
                <Typography variant="caption">{props.formSubmission.privilegedUsersLabel}</Typography>
              </Tooltip>
            </span>,
          );
        }

        return result;
      }
      case FormSubmissionPrivacyType.RecipientOnly: {
        const sender = props.formSubmission.createdByUser;
        const recipient = props.formSubmission.recipientUser;
        const uniquelyVisibleTo = _.uniqBy([sender, recipient], (x) => x.userId);

        return uniquelyVisibleTo.map((user, index, array) => (
          <span key={index}>
            <Link href={Links.User(user.userId)} onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
              {user.displayName}
            </Link>
            {index < array.length - 1 && ", "}
          </span>
        ));
      }
    }
  }

  const isDraft = props.formSubmission.submittedDateTimeUtc == null && props.onDraftClicked != null;

  const cardContent = (
    <Stack>
      {(isDraft || props.formSubmission.requiresReviewByRecipient || props.formSubmission.requiresReviewInOneOnOne) && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
          {isDraft ? (
            <Chip label="Draft" color="secondary" />
          ) : (
            <>
              {props.formSubmission.requiresReviewByRecipient &&
              props.formSubmission.reviewedByRecipientDateTimeUtc != null ? (
                <Chip label="Reviewed By Recipient" variant="tertiary" icon={<CheckRounded fontSize="small" />} />
              ) : (
                <Chip
                  label="Review By Recipient Pending"
                  color="warning"
                  icon={<PriorityHighRounded fontSize="small" />}
                />
              )}

              {props.formSubmission.requiresReviewInOneOnOne && props.formSubmission.reviewedInOneOnOne ? (
                <Chip label="Reviewed in 1:1" variant="tertiary" icon={<CheckRounded fontSize="small" />} />
              ) : (
                <Chip label="Review in 1:1 Pending" color="warning" icon={<PriorityHighRounded fontSize="small" />} />
              )}
            </>
          )}
        </Stack>
      )}

      <Typography variant="h5">
        {getFormSubmissionName(
          props.formSubmission.formName,
          props.formSubmission.dateRangeStartUtc,
          props.formSubmission.dateRangeEndUtc,
        )}
      </Typography>

      {props.formSubmission.recipientType !== FormRecipientType.Self && (
        <Typography>
          By {props.formSubmission.createdByUser.displayName} to {props.formSubmission.recipientUser.displayName}
        </Typography>
      )}

      <Stack
        direction="row"
        spacing={0.5}
        divider={<Divider orientation="vertical" flexItem sx={{ height: "15px", alignSelf: "center" }} />}
      >
        <Typography variant="caption">Visible to {getVisibility()}</Typography>

        <Typography variant="caption">
          <span>Created </span>
          {DateTime.fromISO(props.formSubmission.createdDateTimeUtc, { zone: "utc" })
            .toLocal()
            .toLocaleString(DateTime.DATE_MED)}
        </Typography>

        {props.formSubmission.submittedDateTimeUtc != null && (
          <Typography variant="caption">
            <span>Submitted </span>
            {DateTime.fromISO(props.formSubmission.submittedDateTimeUtc, { zone: "utc" })
              .toLocal()
              .toLocaleString(DateTime.DATE_MED)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  if (isDraft) {
    return (
      <StyledCard
        elevation={0}
        onClick={() => props.onDraftClicked!(props.formSubmission.formSubmissionGuid, props.formSubmission.formGuid)}
      >
        {cardContent}
      </StyledCard>
    );
  }

  return (
    <Link
      href={Links.UserFeedbackDetails(props.formSubmission.formSubmissionGuid)}
      onClick={(e) => props.onClicked?.(e, props.formSubmission.formSubmissionGuid)}
      sx={{ "&:hover": { textDecoration: "none" } }}
    >
      <StyledCard elevation={0}>{cardContent}</StyledCard>
    </Link>
  );
};

export { UserFeedbackListItem };
